import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ensureSharedPusher } from '@/services/sharedPusherClient'
import { selectCurrentUser } from '@/store/account'
import { updateMarketSportsEvent } from '@/store/markets'

const SPORTS_PUSHER_CHANNEL = 'event'
const SPORTS_PUSHER_EVENT = 'live-data-update'

// Top-level question fields from live-data-update (see backend contract).
const QUESTION_FIELDS = [
  'event_start_date',
  'event_end_date',
  'is_live',
  'status',
  'resolution',
  'resolve_date',
  'is_wagerable',
]

// Optional top-level overrides; primary payload lives under event_extra_data.
const EVENT_EXTRA_DATA_FIELDS = [
  'home_team',
  'away_team',
  'home_team_short_code',
  'away_team_short_code',
  'home_team_logo',
  'away_team_logo',
  'home_score',
  'away_score',
  'home_score_regular_time',
  'away_score_regular_time',
  'home_score_extra_time',
  'away_score_extra_time',
  'home_score_penalties',
  'away_score_penalties',
  'source_status',
  'match_status',
  'match_status_display',
  'minute',
  'match_minute',
  'current_minute',
  'live_minute',
  'time_minute',
  'added_time',
  'injury_time',
  'extra_minute',
  'added_minute',
  'final_type',
  'livescore_updated_at',
  'data_freshness_seconds',
  'linked_from_manual',
  'linked_from_manual_at',
]

const parseSportsPusherPayload = data => {
  if (!data) return null

  const marketId = data.question_id ?? data.id
  if (marketId == null) return null

  const { is_live: nestedIsLive, ...nestedExtraData } = data.event_extra_data || {}
  const eventExtraData = { ...nestedExtraData }

  for (const key of EVENT_EXTRA_DATA_FIELDS) {
    if (data[key] !== undefined) eventExtraData[key] = data[key]
  }

  const patch = {}
  if (Object.keys(eventExtraData).length) patch.event_extra_data = eventExtraData

  for (const key of QUESTION_FIELDS) {
    if (data[key] !== undefined) patch[key] = data[key]
  }

  if (patch.is_live === undefined && nestedIsLive !== undefined) {
    patch.is_live = nestedIsLive
  }

  if (!Object.keys(patch).length) return null

  return { marketId, patch }
}

const isDev = import.meta.env.DEV

const maskPusherKey = key => {
  if (!key || key.length < 8) return '(missing or too short)'
  return `${key.slice(0, 4)}…${key.slice(-4)}`
}

const log = (...args) => {
  if (isDev) console.log('[sports-pusher]', ...args)
}

const logReceived = (eventName, data, parsed) => {
  if (!isDev) return
  console.log(
    `%c[sports-pusher] ${eventName} RECEIVED`,
    'color: #e11d48; font-weight: bold',
    { raw: data, parsed, dispatched: Boolean(parsed) }
  )
}

const logStartupConfig = () => {
  if (!isDev) return

  const key = import.meta.env.VITE_PUSHER_APP_KEY
  const cluster = import.meta.env.VITE_PUSHER_APP_CLUSTER

  log('startup', {
    mode: import.meta.env.MODE,
    dev: isDev,
    nodeEnv: import.meta.env.NODE_ENV,
    pusherConfigured: Boolean(key && cluster),
    pusherKey: maskPusherKey(key),
    pusherCluster: cluster || '(missing)',
    channel: SPORTS_PUSHER_CHANNEL,
    event: SPORTS_PUSHER_EVENT,
    filterHint: 'Filter console by "[sports-pusher]"',
  })

  if (!key || !cluster) {
    console.warn(
      '[sports-pusher] VITE_PUSHER_APP_KEY and VITE_PUSHER_APP_CLUSTER must be set in .env — live-data-update will not be received'
    )
  }
}

const subscribeToSportsEvents = (dispatch, userId) => {
  let cancelled = false
  let channel = null

  logStartupConfig()

  const onSubscriptionSucceeded = () => {
    log('subscription_succeeded', { channel: SPORTS_PUSHER_CHANNEL, listeningFor: SPORTS_PUSHER_EVENT })
  }

  const onSubscriptionError = status => {
    console.warn('[sports-pusher] subscription_error', SPORTS_PUSHER_CHANNEL, status)
  }

  const onSportsEvent = data => {
    const parsed = parseSportsPusherPayload(data)
    logReceived(SPORTS_PUSHER_EVENT, data, parsed)

    if (!parsed) {
      log('event_ignored', 'missing question_id/id or no patchable fields', data)
      return
    }

    dispatch(updateMarketSportsEvent(parsed))
    log('redux_dispatched', 'updateMarketSportsEvent', parsed)
  }

  ;(async () => {
    if (!import.meta.env.VITE_PUSHER_APP_KEY || !import.meta.env.VITE_PUSHER_APP_CLUSTER) {
      return
    }

    const pusher = await ensureSharedPusher(userId ?? null)
    if (cancelled || !pusher) {
      log('skipped', cancelled ? 'cancelled' : 'pusher unavailable')
      return
    }

    log('subscribing', { channel: SPORTS_PUSHER_CHANNEL, event: SPORTS_PUSHER_EVENT, userId: userId ?? 'anon' })
    channel = pusher.subscribe(SPORTS_PUSHER_CHANNEL)
    channel.bind('pusher:subscription_succeeded', onSubscriptionSucceeded)
    channel.bind('pusher:subscription_error', onSubscriptionError)
    channel.bind(SPORTS_PUSHER_EVENT, onSportsEvent)
  })()

  return () => {
    cancelled = true
    channel?.unbind('pusher:subscription_succeeded', onSubscriptionSucceeded)
    channel?.unbind('pusher:subscription_error', onSubscriptionError)
    channel?.unbind(SPORTS_PUSHER_EVENT, onSportsEvent)
    log('unsubscribed', SPORTS_PUSHER_CHANNEL, SPORTS_PUSHER_EVENT)
  }
}

/** Live sports scores / match state via Pusher → Redux → SportEventQuestionHeader */
const useRealTimeSportsEvent = () => {
  const dispatch = useDispatch()
  const userId = useSelector(selectCurrentUser)?.id

  useEffect(() => {
    if (import.meta.env.NODE_ENV === 'test') return
    if (!import.meta.env.VITE_PUSHER_APP_KEY || !import.meta.env.VITE_PUSHER_APP_CLUSTER) return
    return subscribeToSportsEvents(dispatch, userId)
  }, [dispatch, userId])
}

export default useRealTimeSportsEvent
